import React from 'react';
import { Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { ListChecks } from 'lucide-react';
import { REPEAT_LABEL, memberOf, type WorkTask } from './data';
import { STATUS_META, daysLate, isOverdue } from './flow';

export const fmtDate = (d?: string) => (d ? dayjs(d).format('DD/MM/YYYY') : '—');

export function Avatar({ id, size = 26, withName }: { id?: string; size?: number; withName?: boolean }) {
  const m = memberOf(id);
  const dot = (
    <span className="inline-flex shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
      style={{ width: size, height: size, background: m?.color ?? '#e2e8f0' }}>
      {m ? m.name.split(' ').pop()?.[0] : '?'}
    </span>
  );
  if (withName) return <span className="inline-flex min-w-0 items-center gap-2">{dot}<span className={`truncate ${m ? '' : 'text-red-500'}`}>{m?.name ?? 'Chưa giao'}</span></span>;
  return <Tooltip title={m?.name ?? 'Chưa giao'}>{dot}</Tooltip>;
}

export function StatusTag({ t }: { t: WorkTask }) {
  if (isOverdue(t)) return <Tag color="error" className="!m-0">Quá hạn</Tag>;
  const s = STATUS_META[t.status];
  return <Tag color={s.tag} className="!m-0">{s.label}</Tag>;
}

export function PriorityTag({ t }: { t: WorkTask }) {
  if (t.important && t.urgent) return <Tag color="red" className="!m-0">Gấp & quan trọng</Tag>;
  if (t.important) return <Tag color="orange" className="!m-0">Quan trọng</Tag>;
  if (t.urgent) return <Tag color="gold" className="!m-0">Khẩn</Tag>;
  return null;
}

export function DueText({ t }: { t: WorkTask }) {
  if (!t.dueAt) return <span className="text-slate-400">Không hạn</span>;
  const late = isOverdue(t) ? daysLate(t) : 0;
  const soon = !late && t.status !== 'done' && dayjs(t.dueAt).diff(dayjs().startOf('day'), 'day') <= 1 && !['failed', 'cancelled', 'review'].includes(t.status);
  return (
    <span className={late ? 'text-red-500' : soon ? 'text-amber-600' : ''}>
      {fmtDate(t.dueAt)}
      {late > 0 && <div className="text-xs">trễ {late} ngày</div>}
      {soon && <div className="text-xs">sắp đến hạn</div>}
    </span>
  );
}

export function RepeatTag({ t }: { t: WorkTask }) {
  if (!t.repeat) return null;
  return <Tag color="cyan" className="!m-0">{REPEAT_LABEL[t.repeat]}</Tag>;
}

/** Tiến độ checklist dạng "3/5" — xanh khi đủ */
export function CheckCount({ t }: { t: WorkTask }) {
  const list = t.checklist ?? [];
  if (!list.length) return null;
  const done = list.filter(c => c.done).length;
  return (
    <Tooltip title="Checklist quy trình">
      <span className={`inline-flex items-center gap-1 text-xs ${done === list.length ? 'text-green-600' : 'text-slate-500'}`}><ListChecks size={13} />{done}/{list.length}</span>
    </Tooltip>
  );
}

/** Nén ảnh trước khi lưu (localStorage có giới hạn) — cạnh dài tối đa 960px, JPEG */
export function compressImage(file: File, max = 960, quality = 0.7): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Không đọc được ảnh'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('File không phải ảnh'));
      img.onload = () => {
        const k = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * k);
        canvas.height = Math.round(img.height * k);
        canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

export const Field =({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start gap-3 py-1.5 text-sm">
    <span className="w-28 shrink-0 pt-1 text-slate-500">{label}</span>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);
